import { FC, useMemo } from "react";
import type { MouseEvent } from "react";
import { Box, IconButton, Tooltip, Icon } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { TitleBarProps } from "./title-bar";

/**
 * @param TitleBarProps default properties needed to display icons in the title bar
 * @param {Function} onWindowClose Callback to parent to handle any additional actions when the window closes
 */
type TitleBarActionsProps = TitleBarProps & {
  onWindowClose: () => void;
};

/**
 * This component handles the display of any action buttons and `close` button,
 * and are positioned in the top right of a draggable window.
 *
 * @param props TitleBarActionsProps
 * @returns Component with action and close buttons
 */
const TitleBarActions: FC<TitleBarActionsProps> = (props) => {
  const { actions, titleColor, onWindowClose } = props;

  const closeButton = useMemo(() => {
    return (
      <div className="close_x_button_box">
        <IconButton
          className="close_x_button"
          size="small"
          onClick={onWindowClose}
          aria-label="Close"
          sx={{ cursor: "pointer", color: "inherit" }}
        >
          <CloseIcon />
        </IconButton>
      </div>
    );
  }, [onWindowClose]);

  return (
    <Box component="div" sx={{ flexGrow: 0 }} className="title-action">
      <Box component="div" sx={{ display: "flex", alignItems: "center" }}>
        {actions?.map((action) => {
          let color: string | undefined = undefined;
          if (!action.disabled) {
            color = titleColor ?? "#FFF";
          }
          return (
            <Box component="div" key={action.name} sx={{ flexGrow: 0 }}>
              <Tooltip title={action.tooltip} placement="top">
                <IconButton
                  aria-label={action.tooltip}
                  onMouseDown={(e: MouseEvent) => e.preventDefault()}
                  onClick={() => {
                    action.actionFunction();
                  }}
                  sx={{ color: color }}
                  disabled={action.disabled}
                >
                  <Icon>{action.icon}</Icon>
                </IconButton>
              </Tooltip>
            </Box>
          );
        })}
        {closeButton}
      </Box>
    </Box>
  );
};

export default TitleBarActions;
